import React, { useEffect, useMemo, useRef, useState } from "react";
import { track as metaTrack } from "../lib/metaPixel";

// XX.281 — Second Innings Cricket · GTA demand-validation landing.
// Mobile-first, Meta-ad-optimised. Separate sub-brand from Gully;
// "Powered by Gully" appears only in a subtle footer line.
//
// All copy is verbatim from the operator's brief. Analytics events
// (page_view / cta_click / form_start) plus registration writes go
// through the dedicated /api/second-innings/* endpoints so this
// experiment does not pollute the main Gully acquisition funnel.

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

// ---- Utilities -----------------------------------------------------------
function parseUtm() {
  if (typeof window === "undefined") return {};
  const p = new URLSearchParams(window.location.search);
  const keys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
  const out = {};
  keys.forEach((k) => {
    const v = p.get(k);
    if (v) out[k] = v.slice(0, 120);
  });
  return out;
}

function getOrCreateBrowserId() {
  if (typeof window === "undefined") return null;
  try {
    let bid = localStorage.getItem("gully_browser_id");
    if (!bid) {
      bid = "sib_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem("gully_browser_id", bid);
    }
    return bid;
  } catch {
    return null;
  }
}

function detectPlatform() {
  if (typeof navigator === "undefined") return "web";
  const ua = (navigator.userAgent || "").toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return "ios";
  if (/android/.test(ua)) return "android";
  return "web";
}

async function postEvent(event_name, ctx) {
  if (!API_BASE) return;
  try {
    await fetch(`${API_BASE}/api/second-innings/events`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ event_name, ...ctx }),
      keepalive: true,
    });
  } catch {
    /* fire-and-forget */
  }
}

// ---- UI atoms ------------------------------------------------------------
const LEVEL_OPTIONS = [
  { v: "new",          label: "New to cricket" },
  { v: "beginner",     label: "Beginner" },
  { v: "recreational", label: "Recreational" },
  { v: "competitive",  label: "Competitive" },
];
const ACTIVITY_OPTIONS = [
  { v: "just_play",     label: "Just play" },
  { v: "nets",          label: "Nets / practice" },
  { v: "challenges",    label: "Challenges" },
  { v: "improve",       label: "Improve my cricket" },
  { v: "meet_others",   label: "Meet other cricketers" },
];
const WTP_OPTIONS = [
  { v: "yes",   label: "Yes" },
  { v: "maybe", label: "Maybe" },
  { v: "no",    label: "No" },
];
const TIMING_OPTIONS = [
  { v: "weekday_evening", label: "Weekday evening" },
  { v: "friday_evening",  label: "Friday evening" },
  { v: "saturday",        label: "Saturday" },
  { v: "sunday",          label: "Sunday" },
];

function RadioPillGroup({ name, value, options, onChange, testIdPrefix, invalid, groupRef }) {
  return (
    <div
      ref={groupRef}
      tabIndex={invalid ? -1 : undefined}
      className={invalid ? "rounded-xl ring-2 ring-red-500/70 ring-offset-2 ring-offset-[#0A0F1D] p-2 -m-2" : ""}
    >
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={name}>
        {options.map((opt) => {
          const active = value === opt.v;
          return (
            <button
              key={opt.v}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(opt.v)}
              data-testid={`${testIdPrefix}-${opt.v}`}
              className={`px-4 py-2.5 rounded-full border text-sm font-semibold transition-all active:scale-[0.98] ${
                active
                  ? "bg-[#CCFF00] text-black border-[#CCFF00] shadow-[0_0_18px_rgba(204,255,0,0.35)]"
                  : "bg-slate-900/70 text-slate-200 border-slate-700 hover:border-slate-500"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      {invalid && (
        <p className="mt-2 text-xs font-semibold text-red-400" data-testid={`${testIdPrefix}-required`}>
          Please pick one to continue
        </p>
      )}
    </div>
  );
}

function FieldLabel({ children, htmlFor }) {
  return (
    <label
      htmlFor={htmlFor}
      className="text-xs uppercase tracking-widest font-bold text-slate-400 mb-2 block"
    >
      {children}
    </label>
  );
}

// ---- Main page -----------------------------------------------------------
export default function SecondInningsCricket() {
  const utmRef = useRef(parseUtm());
  const bidRef = useRef(getOrCreateBrowserId());
  const platformRef = useRef(detectPlatform());
  const referrerRef = useRef(typeof document !== "undefined" ? (document.referrer || "").slice(0, 500) : "");

  const analyticsCtx = useMemo(
    () => ({
      utm: utmRef.current,
      referrer: referrerRef.current || null,
      client_platform: platformRef.current,
      browser_id: bidRef.current,
      session_id: null,
    }),
    []
  );

  // Fire page_view once on mount.
  useEffect(() => {
    postEvent("page_view", analyticsCtx);
    // Set document.title for share cards.
    if (typeof document !== "undefined") {
      document.title = "Second Innings Cricket — Just show up. Play cricket. Have fun.";
    }
  }, [analyticsCtx]);

  // Form state
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [level, setLevel] = useState("");
  const [activity, setActivity] = useState("");
  const [wtp, setWtp] = useState("");
  const [timing, setTiming] = useState("");
  const [consent, setConsent] = useState(false);   // XX.281b — must be actively checked
  const [formStartedFired, setFormStartedFired] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [wasDuplicate, setWasDuplicate] = useState(false);
  const [error, setError] = useState("");
  // XX.281c — conversion-friction fix: keep submit enabled, but track
  // which required fields are missing after a submit attempt so we can
  // highlight them and scroll to the first offender.
  const [missing, setMissing] = useState({});   // { fieldKey: true }
  const [attempted, setAttempted] = useState(false);

  // Refs used to scroll/focus the first missing field on mobile.
  const firstNameRef = useRef(null);
  const emailRef     = useRef(null);
  const cityRef      = useRef(null);
  const levelRef     = useRef(null);
  const activityRef  = useRef(null);
  const wtpRef       = useRef(null);
  const timingRef    = useRef(null);
  const consentRef   = useRef(null);

  const emailValid = /.+@.+\..+/.test(email);

  function computeMissing() {
    return {
      firstName: firstName.trim().length === 0,
      email:     !emailValid,
      city:      city.trim().length === 0,
      level:     !level,
      activity:  !activity,
      wtp:       !wtp,
      timing:    !timing,
      consent:   !consent,
    };
  }

  // Recompute the highlight set once the user has attempted a submit so
  // fields un-highlight as they fill them in.
  useEffect(() => {
    if (!attempted) return;
    setMissing(computeMissing());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstName, email, city, level, activity, wtp, timing, consent, attempted]);

  const MISSING_LABELS = {
    firstName: "First name",
    email:     "Email",
    city:      "City",
    level:     "Cricket level",
    activity:  "What you'd most want",
    wtp:       "Willingness to pay",
    timing:    "Preferred timing",
    consent:   "Consent checkbox",
  };

  const FIELD_ORDER = ["firstName", "email", "city", "level", "activity", "wtp", "timing", "consent"];
  const REFS = {
    firstName: firstNameRef,
    email:     emailRef,
    city:      cityRef,
    level:     levelRef,
    activity:  activityRef,
    wtp:       wtpRef,
    timing:    timingRef,
    consent:   consentRef,
  };

  function focusFirstMissing(m) {
    const firstKey = FIELD_ORDER.find((k) => m[k]);
    if (!firstKey) return;
    const el = REFS[firstKey]?.current;
    if (!el) return;
    try {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      // Delay focus so the smooth scroll completes on mobile.
      setTimeout(() => {
        try { el.focus({ preventScroll: true }); } catch { /* noop */ }
      }, 350);
    } catch { /* noop */ }
  }

  const missingList = attempted
    ? FIELD_ORDER.filter((k) => missing[k]).map((k) => MISSING_LABELS[k])
    : [];

  function markFormStarted() {
    if (formStartedFired) return;
    setFormStartedFired(true);
    postEvent("form_start", analyticsCtx);
  }

  function jumpToForm() {
    postEvent("cta_click", { ...analyticsCtx, cta: "join_first_group" });
    const el = document.getElementById("registration-form");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (submitting) return;
    // XX.281c — validate first, highlight missing fields instead of a
    // dead-disabled button. Keep the same validation requirements.
    const m = computeMissing();
    const hasMissing = Object.values(m).some(Boolean);
    if (hasMissing) {
      setAttempted(true);
      setMissing(m);
      focusFirstMissing(m);
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/second-innings/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: firstName.trim(),
          email: email.trim().toLowerCase(),
          phone: phone.trim() || null,
          city: city.trim(),
          cricket_level: level,
          desired_activity: activity,
          willingness_to_pay: wtp,
          preferred_timing: timing,
          consent_updates: !!consent,
          utm: utmRef.current,
          referrer: referrerRef.current || null,
          client_platform: platformRef.current,
          browser_id: bidRef.current,
          session_id: null,
        }),
      });
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      // XX.281c — parse response to detect duplicate email. Backend
      // returns { ok: true, duplicate: true } when the email already
      // exists. In that case we DO NOT fire the SecondInningsRegistration
      // Meta Pixel event (avoids double-counting a lead), and we show a
      // friendly, success-equivalent message.
      let isDuplicate = false;
      try {
        const data = await res.json();
        isDuplicate = !!(data && data.duplicate);
      } catch { /* body optional */ }

      if (!isDuplicate) {
        // XX.281b — Meta Pixel custom event, fires ONLY on confirmed
        // successful *new* registration. Uses existing pixel infra via
        // metaPixel.track(). `SecondInningsRegistration` is intentionally
        // NOT in STANDARD_EVENT_MIRROR — this keeps it isolated from
        // Gully's Lead / CompleteRegistration / Purchase mirrors.
        try {
          metaTrack("SecondInningsRegistration", {
            utm_source:   utmRef.current.utm_source || null,
            utm_medium:   utmRef.current.utm_medium || null,
            utm_campaign: utmRef.current.utm_campaign || null,
          });
        } catch { /* pixel is best-effort — never break the app */ }
      }
      setWasDuplicate(isDuplicate);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="min-h-screen bg-[#0A0F1D] text-white antialiased"
      data-testid="second-innings-page"
    >
      {/* Stadium spotlight overlay */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10"
        style={{
          background:
            "radial-gradient(ellipse 80% 50% at 50% -20%, rgba(204,255,0,0.15), transparent 70%), radial-gradient(circle at 50% 100%, rgba(56,189,248,0.12), transparent 60%)",
        }}
      />

      {/* -------- Hero -------- */}
      <section className="px-5 pt-10 pb-8 sm:pt-16 sm:pb-14 max-w-2xl mx-auto">
        <div className="inline-flex items-center gap-2 rounded-full border border-slate-700/70 bg-slate-900/50 px-3 py-1.5 mb-6" data-testid="second-innings-badge">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#CCFF00] opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#CCFF00]" />
          </span>
          <span className="text-xs uppercase tracking-widest font-bold text-slate-300">
            GTA · First session planned for last week of October
          </span>
        </div>

        <h1
          className="font-black uppercase tracking-tight leading-[0.95] text-4xl sm:text-5xl lg:text-6xl"
          style={{ fontFamily: "Barlow Condensed, Impact, sans-serif" }}
          data-testid="second-innings-headline"
        >
          Just show up.
          <br />
          Play cricket.
          <br />
          <span className="text-[#CCFF00]">Have fun.</span>
        </h1>

        <p className="mt-6 text-lg sm:text-xl text-slate-300 max-w-lg" data-testid="second-innings-subhead">
          Two hours of cricket with people who love cricket.
        </p>

        <button
          type="button"
          onClick={jumpToForm}
          data-testid="second-innings-hero-cta"
          className="mt-8 inline-flex items-center gap-2 rounded-xl bg-[#CCFF00] text-black font-black uppercase tracking-wider px-6 py-4 text-base shadow-[0_0_24px_rgba(204,255,0,0.30)] active:scale-[0.98] transition-transform"
        >
          Join the first group
        </button>
      </section>

      {/* -------- Inclusivity copy -------- */}
      <section className="px-5 py-8 max-w-2xl mx-auto">
        <div className="space-y-3 text-slate-300 text-base sm:text-lg leading-relaxed">
          <p className="text-white font-semibold">It doesn't matter who you play for.</p>
          <p>
            Competitive player. Recreational cricketer. Getting back into the game.
            New to cricket. Or just someone who loves playing.
          </p>
          <p>Come alone or come with friends.</p>
          <p>Bat. Bowl. Play. Try some challenges. Meet other cricketers.</p>
          <p className="text-slate-400">No league commitment. No team required.</p>
          <p className="text-white font-semibold">Just cricket.</p>
        </div>
      </section>

      {/* -------- What to expect -------- */}
      <section className="px-5 py-8 max-w-2xl mx-auto" data-testid="second-innings-what-to-expect">
        <h2
          className="text-2xl sm:text-3xl font-extrabold uppercase tracking-tight text-white mb-6"
          style={{ fontFamily: "Barlow Condensed, Impact, sans-serif" }}
        >
          What to expect
        </h2>
        <ul className="space-y-3">
          {[
            { icon: "🏏", text: "Bat & bowl" },
            { icon: "🎯", text: "Cricket challenges" },
            { icon: "⚡", text: "Quick games" },
            { icon: "🤝", text: "Meet other GTA cricketers" },
            { icon: "😄", text: "Two hours of cricket and fun" },
          ].map((it) => (
            <li
              key={it.text}
              className="flex items-center gap-4 rounded-xl border border-slate-800 bg-slate-900/50 px-4 py-3"
            >
              <span className="text-2xl" aria-hidden>{it.icon}</span>
              <span className="text-slate-100 text-base sm:text-lg font-medium">{it.text}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* -------- Form -------- */}
      <section id="registration-form" className="px-5 py-10 max-w-2xl mx-auto scroll-mt-4">
        {submitted ? (
          <div
            className="rounded-2xl border border-[#CCFF00]/40 bg-slate-900/70 p-6 sm:p-8 shadow-[0_0_32px_rgba(204,255,0,0.20)]"
            data-testid={wasDuplicate ? "second-innings-success-duplicate" : "second-innings-success"}
          >
            <h2
              className="text-3xl sm:text-4xl font-black uppercase tracking-tight text-[#CCFF00] mb-4"
              style={{ fontFamily: "Barlow Condensed, Impact, sans-serif" }}
            >
              {wasDuplicate ? "You're already in." : "You're in."}
            </h2>
            {wasDuplicate ? (
              <p className="text-slate-200 text-base sm:text-lg leading-relaxed">
                You're already on the list — we'll make sure you get first access
                when the first Second Innings cricket session in the GTA is confirmed.
              </p>
            ) : (
              <>
                <p className="text-slate-200 text-base sm:text-lg leading-relaxed">
                  We're putting together the first Second Innings cricket session for the GTA,
                  planned for the last week of October.
                </p>
                <p className="text-slate-200 text-base sm:text-lg leading-relaxed mt-3">
                  We'll contact you first when the details are ready.
                </p>
              </>
            )}
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            onFocus={markFormStarted}
            data-testid="second-innings-form"
            className="rounded-2xl border border-slate-800 bg-slate-900/70 p-5 sm:p-7 space-y-6"
          >
            <h2
              className="text-2xl sm:text-3xl font-extrabold uppercase tracking-tight text-white"
              style={{ fontFamily: "Barlow Condensed, Impact, sans-serif" }}
            >
              Claim your spot
            </h2>

            <div>
              <FieldLabel htmlFor="fn">First name</FieldLabel>
              <input
                id="fn"
                ref={firstNameRef}
                type="text"
                autoComplete="given-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                data-testid="second-innings-input-first-name"
                aria-invalid={!!missing.firstName}
                className={`w-full bg-slate-950 border rounded-xl px-4 py-3.5 text-white placeholder-slate-500 outline-none text-base ${
                  missing.firstName
                    ? "border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500"
                    : "border-slate-700 focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00]"
                }`}
                placeholder="Your first name"
              />
              {missing.firstName && (
                <p className="mt-1.5 text-xs font-semibold text-red-400" data-testid="second-innings-firstname-required">
                  Please add your first name
                </p>
              )}
            </div>

            <div>
              <FieldLabel htmlFor="em">Email</FieldLabel>
              <input
                id="em"
                ref={emailRef}
                type="email"
                autoComplete="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="second-innings-input-email"
                aria-invalid={!!missing.email}
                className={`w-full bg-slate-950 border rounded-xl px-4 py-3.5 text-white placeholder-slate-500 outline-none text-base ${
                  missing.email
                    ? "border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500"
                    : "border-slate-700 focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00]"
                }`}
                placeholder="you@email.com"
              />
              {missing.email && (
                <p className="mt-1.5 text-xs font-semibold text-red-400" data-testid="second-innings-email-required">
                  {email.trim().length === 0 ? "Please add your email" : "Please enter a valid email"}
                </p>
              )}
            </div>

            <div>
              <FieldLabel htmlFor="ph">Phone number (optional)</FieldLabel>
              <input
                id="ph"
                type="tel"
                autoComplete="tel"
                inputMode="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                data-testid="second-innings-input-phone"
                className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-3.5 text-white placeholder-slate-500 focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00] outline-none text-base"
                placeholder="+1 416 ..."
              />
            </div>

            <div>
              <FieldLabel htmlFor="ci">City</FieldLabel>
              <input
                id="ci"
                ref={cityRef}
                type="text"
                autoComplete="address-level2"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                data-testid="second-innings-input-city"
                aria-invalid={!!missing.city}
                className={`w-full bg-slate-950 border rounded-xl px-4 py-3.5 text-white placeholder-slate-500 outline-none text-base ${
                  missing.city
                    ? "border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500"
                    : "border-slate-700 focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00]"
                }`}
                placeholder="e.g. Toronto, Brampton, Mississauga"
              />
              {missing.city && (
                <p className="mt-1.5 text-xs font-semibold text-red-400" data-testid="second-innings-city-required">
                  Please add your city
                </p>
              )}
            </div>

            <div>
              <FieldLabel>Cricket level</FieldLabel>
              <RadioPillGroup
                name="cricket-level"
                value={level}
                options={LEVEL_OPTIONS}
                onChange={setLevel}
                testIdPrefix="second-innings-level"
                invalid={!!missing.level}
                groupRef={levelRef}
              />
            </div>

            <div>
              <FieldLabel>What would you most want?</FieldLabel>
              <RadioPillGroup
                name="desired-activity"
                value={activity}
                options={ACTIVITY_OPTIONS}
                onChange={setActivity}
                testIdPrefix="second-innings-activity"
                invalid={!!missing.activity}
                groupRef={activityRef}
              />
            </div>

            <div>
              <FieldLabel>Would you pay approximately CAD $15–20 for a two-hour indoor cricket session?</FieldLabel>
              <RadioPillGroup
                name="willingness-to-pay"
                value={wtp}
                options={WTP_OPTIONS}
                onChange={setWtp}
                testIdPrefix="second-innings-wtp"
                invalid={!!missing.wtp}
                groupRef={wtpRef}
              />
            </div>

            <div>
              <FieldLabel>Preferred timing</FieldLabel>
              <RadioPillGroup
                name="preferred-timing"
                value={timing}
                options={TIMING_OPTIONS}
                onChange={setTiming}
                testIdPrefix="second-innings-timing"
                invalid={!!missing.timing}
                groupRef={timingRef}
              />
            </div>

            <div
              ref={consentRef}
              tabIndex={-1}
              className={`rounded-xl transition-all ${
                missing.consent
                  ? "ring-2 ring-red-500/80 ring-offset-2 ring-offset-[#0A0F1D] bg-red-500/5 p-3 -m-1"
                  : ""
              }`}
              data-testid="second-innings-consent-block"
            >
              <label
                className="flex items-start gap-3 text-sm text-slate-300 cursor-pointer"
                data-testid="second-innings-consent-wrap"
              >
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  data-testid="second-innings-consent"
                  aria-invalid={!!missing.consent}
                  className="mt-1 h-5 w-5 accent-[#CCFF00]"
                />
                <span>
                  Send me updates about Second Innings cricket sessions.
                  We won't sign you up for anything else.
                </span>
              </label>
              {missing.consent && (
                <p className="mt-2 ml-8 text-xs font-semibold text-red-400" data-testid="second-innings-consent-required">
                  Please tick this box to continue — we need your OK to email you about the session.
                </p>
              )}
            </div>

            {missingList.length > 0 && (
              <div
                className="rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200"
                data-testid="second-innings-missing-banner"
                role="alert"
                aria-live="polite"
              >
                <div className="font-semibold text-red-100">Almost there — a few things to complete:</div>
                <div className="mt-1 text-red-200">{missingList.join(", ")}</div>
              </div>
            )}

            {error && (
              <div className="text-red-400 text-sm" data-testid="second-innings-form-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="second-innings-submit-button"
              className={`w-full py-4 text-lg font-black uppercase tracking-wider rounded-xl flex items-center justify-center gap-2 transition-all active:scale-[0.98] ${
                submitting
                  ? "bg-slate-800 text-slate-500 cursor-wait"
                  : "bg-[#CCFF00] text-black shadow-[0_0_24px_rgba(204,255,0,0.30)] hover:bg-[#B3E600]"
              }`}
            >
              {submitting ? "Sending..." : "I'm in 🏏"}
            </button>

            <p className="text-xs text-slate-500 leading-relaxed">
              Second Innings is a demand-validation experience. Venue, exact date,
              and capacity are not confirmed yet. We'll only email you about this
              cricket experience.
            </p>
          </form>
        )}
      </section>

      {/* -------- Footer -------- */}
      <footer className="px-5 py-10 max-w-2xl mx-auto text-center">
        <div
          className="text-xs uppercase tracking-[0.25em] text-slate-500"
          data-testid="second-innings-powered-by"
        >
          Powered by Gully
        </div>
      </footer>

      {/* Sticky mobile CTA bar (hidden after submission or when form is in view) */}
      {!submitted && (
        <div className="fixed bottom-0 left-0 right-0 z-20 sm:hidden px-4 pb-3 pt-2 bg-gradient-to-t from-[#0A0F1D] via-[#0A0F1D]/95 to-transparent">
          <button
            type="button"
            onClick={jumpToForm}
            data-testid="second-innings-sticky-cta"
            className="w-full rounded-xl bg-[#CCFF00] text-black font-black uppercase tracking-wider py-3.5 text-base shadow-[0_0_28px_rgba(204,255,0,0.40)]"
          >
            Join the first group 🏏
          </button>
        </div>
      )}
    </div>
  );
}
